import React from 'react';

const STATES = {
  unattuned: { selfActive: false, otherActive: false, fieldActive: false, lineOpacity: 0.2 },
  slowing: { selfActive: true, otherActive: false, fieldActive: false, lineOpacity: 0.3 },
  listening: { selfActive: true, otherActive: true, fieldActive: false, lineOpacity: 0.5 },
  triadic_open: { selfActive: true, otherActive: true, fieldActive: true, lineOpacity: 0.7 },
  immersed: { selfActive: true, otherActive: true, fieldActive: true, lineOpacity: 1 }
};

export default function TriadicVisualizer({ 
  state = 'unattuned', 
  coherence = 0,
  size = 'medium',
  className = ''
}) {
  const config = STATES[state] || STATES.unattuned;
  
  const sizes = {
    tiny: { container: 'w-12 h-12', svg: 48 },
    small: { container: 'w-32 h-32', svg: 128 },
    medium: { container: 'w-48 h-48', svg: 192 },
    large: { container: 'w-64 h-64', svg: 256 }
  };
  
  const s = sizes[size] || sizes.medium;
  const svgSize = s.svg;
  const center = svgSize / 2;
  const radius = svgSize * 0.35;
  
  const selfPos = { x: center, y: center - radius };
  const otherPos = { x: center - radius * 0.866, y: center + radius * 0.5 };
  const fieldPos = { x: center + radius * 0.866, y: center + radius * 0.5 };
  
  const nodeRadius = size === 'tiny' ? 4 : size === 'small' ? 8 : size === 'medium' ? 12 : 16;
  
  return (
    <div className={`relative ${s.container} ${className}`}>
      <svg
        width={svgSize}
        height={svgSize}
        viewBox={`0 0 ${svgSize} ${svgSize}`}
        className="absolute inset-0"
      >
        {/* Connection Lines */}
        <line
          x1={selfPos.x} y1={selfPos.y}
          x2={otherPos.x} y2={otherPos.y}
          stroke="rgba(74, 111, 165, 0.5)"
          strokeWidth={config.selfActive && config.otherActive ? 2 : 1}
          opacity={config.lineOpacity}
          className="transition-all duration-500"
        />
        <line
          x1={otherPos.x} y1={otherPos.y}
          x2={fieldPos.x} y2={fieldPos.y}
          stroke="rgba(42, 157, 143, 0.5)"
          strokeWidth={config.otherActive && config.fieldActive ? 2 : 1}
          opacity={config.lineOpacity}
          className="transition-all duration-500"
        />
        <line
          x1={fieldPos.x} y1={fieldPos.y}
          x2={selfPos.x} y2={selfPos.y}
          stroke="rgba(183, 110, 121, 0.5)"
          strokeWidth={config.fieldActive && config.selfActive ? 2 : 1}
          opacity={config.lineOpacity}
          className="transition-all duration-500"
        />
        
        {/* Center Glow */}
        <circle
          cx={center}
          cy={center}
          r={radius * 0.3 || 5}
          fill="url(#centerGlow)"
          className="triadic-center-glow"
        />
        
        {/* Self Node (Human) */}
        <circle
          cx={selfPos.x}
          cy={selfPos.y}
          r={nodeRadius}
          fill={config.selfActive ? '#4a6fa5' : 'rgba(74, 111, 165, 0.3)'}
          className={config.selfActive ? 'triadic-node-pulse' : ''}
          style={{
            filter: config.selfActive ? 'drop-shadow(0 0 8px rgba(74, 111, 165, 0.6))' : 'none',
            transformOrigin: `${selfPos.x}px ${selfPos.y}px`
          }}
        />
        
        {/* Other Node (ALVIN) */}
        <circle
          cx={otherPos.x}
          cy={otherPos.y}
          r={nodeRadius}
          fill={config.otherActive ? '#2a9d8f' : 'rgba(42, 157, 143, 0.3)'}
          className={config.otherActive ? 'triadic-node-pulse triadic-node-delay-1' : ''}
          style={{
            filter: config.otherActive ? 'drop-shadow(0 0 8px rgba(42, 157, 143, 0.6))' : 'none',
            transformOrigin: `${otherPos.x}px ${otherPos.y}px`
          }}
        />
        
        {/* Field Node (Universal Intelligence) */}
        <circle
          cx={fieldPos.x}
          cy={fieldPos.y}
          r={nodeRadius}
          fill={config.fieldActive ? '#b76e79' : 'rgba(183, 110, 121, 0.3)'}
          className={config.fieldActive ? 'triadic-node-pulse triadic-node-delay-2' : ''}
          style={{
            filter: config.fieldActive ? 'drop-shadow(0 0 8px rgba(183, 110, 121, 0.6))' : 'none',
            transformOrigin: `${fieldPos.x}px ${fieldPos.y}px`
          }}
        />
        
        <defs>
          <radialGradient id="centerGlow" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="rgba(183, 110, 121, 0.4)" />
            <stop offset="100%" stopColor="transparent" />
          </radialGradient>
        </defs>
      </svg>
      
      {(size === 'medium' || size === 'large') && (
        <>
          <span className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-6 text-xs text-etheric/80">
            Self
          </span>
          <span className="absolute bottom-0 left-0 translate-y-4 text-xs text-coherence/80">
            Other
          </span>
          <span className="absolute bottom-0 right-0 translate-y-4 text-xs text-rose-gold/80">
            Field
          </span>
        </>
      )}
    </div>
  );
}
